import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, Sun, Moon } from "lucide-react";
import { BRAND, NAV_LINKS, scrollToId, scrollTop } from "./data";
import { useTheme } from "./ThemeContext";
import { LogoMark } from "./LogoMark";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { theme, toggle } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const ThemeToggle = ({ testid }) => (
    <button
      data-testid={testid}
      onClick={toggle}
      aria-label="Toggle theme"
      className="relative grid place-items-center w-10 h-10 rounded-full hairline border chip hover:border-purple-400/50 transition-colors"
    >
      <AnimatePresence mode="wait" initial={false}>
        {theme === "dark" ? (
          <motion.span key="sun" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.25 }}>
            <Sun className="w-4 h-4 text-amber-300" />
          </motion.span>
        ) : (
          <motion.span key="moon" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.25 }}>
            <Moon className="w-4 h-4 text-indigo-600" />
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  );

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl border-b hairline py-3" : "bg-transparent py-5"
      }`}
      style={scrolled ? { background: "var(--nav-bg)" } : {}}
    >
      <div className="max-w-7xl mx-auto px-5 flex items-center justify-between">
        <button
          data-testid="navbar-brand-logo"
          onClick={() => scrollTop()}
          className="flex items-center gap-3 group"
        >
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-purple-500/40 blur-lg group-hover:bg-purple-400/60 transition-colors" />
            <LogoMark variant="icon" className="relative w-11 h-11" />
          </div>
          <div className="text-left leading-none">
            <p className="font-heading font-extrabold text-lg tracking-tight txt-primary">
              {BRAND.name}
            </p>
            <p className="font-mono text-[10px] tracking-[0.25em] overline uppercase">
              {BRAND.suffix}
            </p>
          </div>
        </button>

        <nav className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`navbar-link-${l.id}`}
              onClick={() => scrollToId(l.id)}
              className="px-4 py-2 text-sm txt-soft hover:txt-primary rounded-full hover:bg-white/5 transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <ThemeToggle testid="theme-toggle" />
          <a
            data-testid="navbar-cta-phone"
            href={`tel:${BRAND.phoneTel}`}
            className="hidden sm:flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] transition-shadow"
          >
            <Phone className="w-4 h-4" />
            <span className="font-mono">{BRAND.phone}</span>
          </a>
          <button
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="md:hidden p-2 txt-primary"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden border-t hairline mt-3"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`navbar-mobile-link-${l.id}`}
                  onClick={() => {
                    scrollToId(l.id);
                    setOpen(false);
                  }}
                  className="text-left px-4 py-3 txt-soft hover:bg-white/5 rounded-xl"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={`tel:${BRAND.phoneTel}`}
                className="mt-2 flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600"
              >
                <Phone className="w-4 h-4" /> {BRAND.phone}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
